import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RefreshCw, TrendingUp, Database, Search as SearchIcon, FileText, CheckCircle, Brain, Sparkles, Package, FolderOpen, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Input } from './ui/input';

function UsageKPIs() {
  const [kpiData, setKpiData] = useState(null);
  const [orderStats, setOrderStats] = useState(null);
  const [loading, setLoading] = useState(false); // Changed to false - don't block on KPI data
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [error, setError] = useState('The /kpis/usage endpoint is not yet implemented');
  const [orderError, setOrderError] = useState(null);
  const [dateRange, setDateRange] = useState('30');
  const [groupBy, setGroupBy] = useState('day');
  const [database, setDatabase] = useState('sunny_orders');
  const [availableDatabases, setAvailableDatabases] = useState(['sunny_orders']);
  const [snapshotDate, setSnapshotDate] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [aiExtractionStats, setAiExtractionStats] = useState(null);
  const [loadingAiStats, setLoadingAiStats] = useState(false);
  const [aiStatsError, setAiStatsError] = useState(null);

  useEffect(() => {
    // Skip fetchKPIData - endpoint not yet implemented
    // fetchKPIData();
    setCurrentPage(1); // Reset to page 1 when filters change
    fetchOrderStats();
    fetchAiExtractionStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, groupBy, database, statusFilter, searchTerm, sortBy, sortDir]);

  useEffect(() => {
    fetchOrderStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, currentPage]);

  const fetchKPIData = async () => {
    setLoading(true);
    setError(null);

    try {
      const end = new Date();
      const start = new Date(end.getTime() - parseInt(dateRange) * 24 * 60 * 60 * 1000);

      const response = await fetch(
        `/api/kpis/usage?startDate=${start.toISOString()}&endDate=${end.toISOString()}&groupBy=${groupBy}`,
        { credentials: 'include' }
      );

      if (!response.ok) throw new Error(`Failed to fetch KPI data: ${response.status}`);

      const data = await response.json();
      setKpiData(data);
    } catch (err) {
      console.error('Error fetching KPI data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderStats = async () => {
    setLoadingOrders(true);
    setOrderError(null);

    try {
      const end = new Date();
      const start = new Date(end.getTime() - parseInt(dateRange) * 24 * 60 * 60 * 1000);
      const offset = (currentPage - 1) * pageSize;

      const params = new URLSearchParams({
        start_date: start.toISOString(),
        end_date: end.toISOString(),
        group_by: groupBy,
        database: database,
        recent_limit: pageSize.toString(),
        recent_offset: offset.toString(),
        sort_by: sortBy,
        sort_dir: sortDir
      });

      if (statusFilter) params.append('status_filter', statusFilter);
      if (searchTerm) params.append('search', searchTerm);

      const response = await fetch(`/api/orders/statistics?${params.toString()}`, { credentials: 'include' });

      if (!response.ok) throw new Error(`Failed to fetch order statistics: ${response.status}`);

      const data = await response.json();
      setOrderStats(data);

      // Update available databases from response
      if (data.availableDatabases) {
        setAvailableDatabases(data.availableDatabases);
      }

      // Update snapshot date if available
      if (data.snapshotDate) {
        setSnapshotDate(data.snapshotDate);
      } else {
        setSnapshotDate(null);
      }

      // Update total orders count for pagination
      if (data.pagination) {
        setTotalOrders(data.pagination.total);
      }
    } catch (err) {
      console.error('Error fetching order statistics:', err);
      setOrderError(err.message);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchAiExtractionStats = async () => {
    setLoadingAiStats(true);
    setAiStatsError(null);

    try {
      const end = new Date();
      const start = new Date(end.getTime() - parseInt(dateRange) * 24 * 60 * 60 * 1000);

      const params = new URLSearchParams({
        database: database,
        start_date: start.toISOString(),
        end_date: end.toISOString()
      });

      const response = await fetch(`/api/ai-extraction/statistics?${params.toString()}`, { credentials: "include" });

      if (!response.ok) throw new Error(`Failed to fetch AI extraction statistics: ${response.status}`);

      const data = await response.json();
      setAiExtractionStats(data);
    } catch (err) {
      console.error("Error fetching AI extraction statistics:", err);
      setAiStatsError(err.message);
    } finally {
      setLoadingAiStats(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle direction
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    } else {
      // New column, default to descending
      setSortBy(column);
      setSortDir('desc');
    }
    setCurrentPage(1);
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return <ArrowUpDown className="h-4 w-4 ml-1 opacity-30" />;
    return sortDir === 'desc' ? <ArrowDown className="h-4 w-4 ml-1" /> : <ArrowUp className="h-4 w-4 ml-1" />;
  };

  const formatDate = (dateStr) => {
    if (groupBy === 'week') return dateStr;
    const date = new Date(dateStr);
    if (groupBy === 'month') {
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const COLORS = {
    publicDataSearches: 'hsl(var(--success))',
    taxSearches: 'hsl(var(--info))',
    titleSearches: 'hsl(262, 83%, 58%)', // Purple - keep as custom
    fulfilled: 'hsl(var(--success))',
    pending: 'hsl(var(--warning))',
    failed: 'hsl(var(--destructive))'
  };

  // Only use kpiData for tabs that need it (API Calls, AI Extraction, Admin View)
  // Order Statistics tab uses its own orderStats data
  const summary = kpiData?.summary;
  const dataTraceSeries = kpiData?.dataTraceSeries || [];
  const recentTransactions = kpiData?.recentTransactions || [];

  const statusData = summary ? [
    { name: 'Fulfilled', value: parseInt(summary.totalOrders * (summary.successRate / 100)), color: COLORS.fulfilled },
    { name: 'Failed', value: summary.totalOrders - parseInt(summary.totalOrders * (summary.successRate / 100)), color: COLORS.failed }
  ] : [];

  return (
    <div className="min-h-screen bg-background p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-foreground">Usage KPIs Dashboard</h1>
        <p className="text-muted-foreground mt-1">Performance metrics and analytics</p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="order-stats" className="space-y-4">
        <TabsList>
          <TabsTrigger value="order-stats">Order Statistics</TabsTrigger>
          <TabsTrigger value="api-calls">API Calls</TabsTrigger>
          <TabsTrigger value="ai-extraction">AI Extraction</TabsTrigger>
          <TabsTrigger value="admin">Admin View</TabsTrigger>
        </TabsList>

      {/* Date Range Controls */}
      <div className="flex gap-2 justify-end">
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Select range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="36500">Lifetime</SelectItem>
          </SelectContent>
        </Select>
        <Select value={groupBy} onValueChange={setGroupBy}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Group by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Daily</SelectItem>
            <SelectItem value="week">Weekly</SelectItem>
            <SelectItem value="month">Monthly</SelectItem>
          </SelectContent>
        </Select>
        <Select value={database} onValueChange={setDatabase}>
          <SelectTrigger className="w-[230px]">
            <SelectValue placeholder="Data source" />
          </SelectTrigger>
          <SelectContent>
            {availableDatabases.map(db => {
              let label = db === 'sunny_orders' ? 'Staging Data' : 'Production Data';
              if (db === 'sunny_orders_production' && snapshotDate) {
                const date = new Date(snapshotDate);
                label += ` (${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})`;
              }
              return (
                <SelectItem key={db} value={db}>
                  {label}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <Button onClick={fetchOrderStats} variant="outline" size="icon">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

        <TabsContent value="order-stats" className="space-y-4">
          {loadingOrders ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="text-muted-foreground">Loading order statistics...</p>
              </div>
            </div>
          ) : orderError ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-destructive">Error Loading Order Statistics</CardTitle>
                <CardDescription>{orderError}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={fetchOrderStats} variant="outline">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Retry
                </Button>
              </CardContent>
            </Card>
          ) : orderStats && orderStats.summary ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
                    <Package className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{orderStats.summary?.totalOrders || 0}</div>
                    <p className="text-xs text-muted-foreground">Created in period</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Draft Orders</CardTitle>
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{orderStats.summary?.ordersByStatus?.draft || 0}</div>
                    <p className="text-xs text-muted-foreground">In progress</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Test Orders</CardTitle>
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-info">{orderStats.summary?.submittedTestOrders || 0}</div>
                    <p className="text-xs text-muted-foreground">Test (no order #)</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Real Orders</CardTitle>
                    <CheckCircle className="h-4 w-4 text-success" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-success">{orderStats.summary?.submittedRealOrders || 0}</div>
                    <p className="text-xs text-muted-foreground">With order number</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">SoftPro Sync</CardTitle>
                    <Database className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{orderStats.summary?.ordersWithSoftPro || 0}</div>
                    <p className="text-xs text-muted-foreground">Synced to SoftPro</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Avg Fields/Order</CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{orderStats.summary?.avgFieldsPerOrder || 0}</div>
                    <p className="text-xs text-muted-foreground">AI extracted</p>
                  </CardContent>
                </Card>
              </div>

              {/* Time Series Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Orders Over Time</CardTitle>
                  <CardDescription>Order creation trends by status (Test vs Real submitted orders)</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={orderStats.timeSeries || []} margin={{ bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={formatDate}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        tick={{ dy: 10 }}
                      />
                      <YAxis />
                      <Tooltip labelFormatter={formatDate} />
                      <Legend />
                      <Line type="monotone" dataKey="total" stroke="#8b5cf6" strokeWidth={3} name="Total Orders" />
                      <Line type="monotone" dataKey="draft" stroke="#f59e0b" strokeWidth={2} name="Draft" />
                      <Line type="monotone" dataKey="submittedTest" stroke="#3b82f6" strokeWidth={2} name="Test Orders" strokeDasharray="5 5" />
                      <Line type="monotone" dataKey="submittedReal" stroke="#10b981" strokeWidth={2} name="Real Orders" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Order Type Breakdown */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Orders by Type</CardTitle>
                    <CardDescription>Refinance vs Purchase breakdown</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={[
                            {
                              name: 'Refinance',
                              value: orderStats.summary?.ordersByType?.refinance || 0,
                              color: 'hsl(var(--info))'
                            },
                            {
                              name: 'Purchase',
                              value: orderStats.summary?.ordersByType?.purchase || 0,
                              color: 'hsl(var(--success))'
                            },
                            {
                              name: 'Other',
                              value: orderStats.summary?.ordersByType?.unknown || 0,
                              color: 'hsl(var(--muted-foreground))'
                            }
                          ].filter(entry => entry.value > 0)}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {[
                            { name: 'Refinance', value: orderStats.summary?.ordersByType?.refinance || 0, color: 'hsl(var(--info))' },
                            { name: 'Purchase', value: orderStats.summary?.ordersByType?.purchase || 0, color: 'hsl(var(--success))' },
                            { name: 'Other', value: orderStats.summary?.ordersByType?.unknown || 0, color: 'hsl(var(--muted-foreground))' }
                          ].filter(entry => entry.value > 0).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Orders by Status</CardTitle>
                    <CardDescription>Draft, Test Orders, Real Orders, Completed</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={[
                            {
                              name: 'Draft',
                              value: orderStats.summary?.ordersByStatus?.draft || 0,
                              color: 'hsl(var(--warning))'
                            },
                            {
                              name: 'Test (No Order #)',
                              value: orderStats.summary?.submittedTestOrders || 0,
                              color: 'hsl(var(--info))'
                            },
                            {
                              name: 'Real Orders',
                              value: orderStats.summary?.submittedRealOrders || 0,
                              color: 'hsl(var(--success))'
                            },
                            {
                              name: 'Completed',
                              value: orderStats.summary?.ordersByStatus?.completed || 0,
                              color: 'hsl(262, 83%, 58%)'
                            }
                          ].filter(entry => entry.value > 0)}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {[
                            { name: 'Draft', value: orderStats.summary?.ordersByStatus?.draft || 0, color: 'hsl(var(--warning))' },
                            { name: 'Test (No Order #)', value: orderStats.summary?.submittedTestOrders || 0, color: 'hsl(var(--info))' },
                            { name: 'Real Orders', value: orderStats.summary?.submittedRealOrders || 0, color: 'hsl(var(--success))' },
                            { name: 'Completed', value: orderStats.summary?.ordersByStatus?.completed || 0, color: 'hsl(262, 83%, 58%)' }
                          ].filter(entry => entry.value > 0).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Refinance Orders by Lender */}
              {orderStats.summary?.refinanceOrdersByLender && Object.keys(orderStats.summary.refinanceOrdersByLender).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Refinance Orders by Lender</CardTitle>
                    <CardDescription>Breakdown of refinance orders by mortgage lender</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={400}>
                      <PieChart>
                        <Pie
                          data={Object.entries(orderStats.summary.refinanceOrdersByLender).map(([name, value]) => ({
                            name,
                            value,
                            color: 'hsl(var(--chart-' + (Object.keys(orderStats.summary.refinanceOrdersByLender).indexOf(name) % 5 + 1) + '))'
                          }))}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={120}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {Object.entries(orderStats.summary.refinanceOrdersByLender).map(([name, value], index) => {
                            const colors = [
                              'hsl(262, 83%, 58%)',  // Purple
                              'hsl(var(--success))',  // Green
                              'hsl(var(--info))',     // Blue
                              'hsl(var(--warning))',  // Yellow/Orange
                              'hsl(var(--destructive))', // Red
                              'hsl(200, 70%, 50%)',   // Cyan
                              'hsl(280, 70%, 50%)',   // Magenta
                              'hsl(160, 70%, 50%)',   // Teal
                              'hsl(30, 70%, 50%)',    // Orange
                              'hsl(300, 70%, 50%)',   // Pink
                              'hsl(180, 70%, 50%)'    // Light Blue
                            ];
                            return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                          })}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Recent Orders Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Orders</CardTitle>
                  <CardDescription>
                    Showing {totalOrders > 0 ? ((currentPage - 1) * pageSize) + 1 : 0}-{Math.min(currentPage * pageSize, totalOrders)} of {totalOrders} total orders
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Filters */}
                  <div className="flex gap-2 mb-4">
                    <div className="flex-1">
                      <div className="relative">
                        <SearchIcon className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search by property address, borrower, or order number..."
                          value={searchTerm}
                          onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                          className="pl-8"
                        />
                      </div>
                    </div>
                    <Select value={statusFilter || "all"} onValueChange={(val) => { setStatusFilter(val === "all" ? "" : val); setCurrentPage(1); }}>
                      <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="All Statuses" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="submitted">Submitted</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2">
                            <button onClick={() => handleSort('internalId')} className="flex items-center hover:text-foreground">
                              Internal ID
                              {getSortIcon('internalId')}
                            </button>
                          </th>
                          <th className="text-left p-2">
                            <button onClick={() => handleSort('orderNumber')} className="flex items-center hover:text-foreground">
                              Order Number
                              {getSortIcon('orderNumber')}
                            </button>
                          </th>
                          <th className="text-left p-2">Property Address</th>
                          <th className="text-left p-2">Borrower</th>
                          <th className="text-left p-2">
                            <button onClick={() => handleSort('orderType')} className="flex items-center hover:text-foreground">
                              Type
                              {getSortIcon('orderType')}
                            </button>
                          </th>
                          <th className="text-left p-2">
                            <button onClick={() => handleSort('status')} className="flex items-center hover:text-foreground">
                              Status
                              {getSortIcon('status')}
                            </button>
                          </th>
                          <th className="text-left p-2">
                            <button onClick={() => handleSort('createdAt')} className="flex items-center hover:text-foreground">
                              Created
                              {getSortIcon('createdAt')}
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(orderStats.recentOrders || []).map((order, idx) => (
                          <tr key={idx} className="border-b hover:bg-muted/50">
                            <td className="p-2 font-mono text-xs">{order.internalId}</td>
                            <td className="p-2">{order.orderNumber || 'N/A'}</td>
                            <td className="p-2">{order.propertyAddress}</td>
                            <td className="p-2">{order.borrowerName}</td>
                            <td className="p-2">
                              <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                                order.orderType === 'refinance' ? 'bg-info/10 text-info ring-1 ring-inset ring-info/20' :
                                order.orderType === 'purchase' ? 'bg-success/10 text-success ring-1 ring-inset ring-success/20' :
                                'bg-muted text-muted-foreground ring-1 ring-inset ring-border'
                              }`}>
                                {order.orderType || 'unknown'}
                              </span>
                            </td>
                            <td className="p-2">
                              <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                                order.status === 'submitted' ? 'bg-success/10 text-success ring-1 ring-inset ring-success/20' :
                                order.status === 'draft' ? 'bg-warning/10 text-warning ring-1 ring-inset ring-warning/20' :
                                order.status === 'completed' ? 'bg-info/10 text-info ring-1 ring-inset ring-info/20' :
                                'bg-muted text-muted-foreground ring-1 ring-inset ring-border'
                              }`}>
                                {order.status || 'unknown'}
                              </span>
                            </td>
                            <td className="p-2 text-xs">
                              {new Date(order.createdAt).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                timeZone: 'America/New_York',
                                timeZoneName: 'short'
                              })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Controls */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Rows per page:</span>
                      <Select value={pageSize.toString()} onValueChange={(val) => { setPageSize(parseInt(val)); setCurrentPage(1); }}>
                        <SelectTrigger className="w-[100px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                          {totalOrders > 0 && <SelectItem value={totalOrders.toString()}>All ({totalOrders})</SelectItem>}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {currentPage} of {Math.max(1, Math.ceil(totalOrders / pageSize))}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage >= Math.ceil(totalOrders / pageSize)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : null}
        </TabsContent>

        <TabsContent value="api-calls" className="space-y-4">
          {!summary || error ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-destructive">API Calls Data Unavailable</CardTitle>
                <CardDescription>
                  {error || 'The /kpis/usage endpoint is not yet implemented. Only Order Statistics are available.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={fetchKPIData} variant="outline">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Try Loading
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Public Data</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.totalPublicDataSearches}</div>
            <p className="text-xs text-muted-foreground">Orders entered</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tax Searches</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.totalTaxSearches}</div>
            <p className="text-xs text-muted-foreground">Tax lookups</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Title Searches</CardTitle>
            <SearchIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.totalTitleSearches}</div>
            <p className="text-xs text-muted-foreground">TIQSA searches</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">TitlePoint</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.totalTitlePointSearches}</div>
            <p className="text-xs text-muted-foreground">Searches</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">{summary.successRate}%</div>
            <p className="text-xs text-muted-foreground">Fulfilled orders</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Public Data Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Public Data Searches Over Time</CardTitle>
                <CardDescription>Property details searches (proxy for orders entered)</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={dataTraceSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDate}
                      angle={-90}
                      textAnchor="end"
                      height={100}
                      interval={0}
                      className="text-xs"
                    />
                    <YAxis />
                    <Tooltip labelFormatter={formatDate} />
                    <Legend />
                    <Bar dataKey="publicDataSearches" fill={COLORS.publicDataSearches} name="Public Data" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Product Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>DataTrace API Usage by Product</CardTitle>
                <CardDescription>Public Data, Tax, and Title Search breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={dataTraceSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDate}
                      angle={-90}
                      textAnchor="end"
                      height={100}
                      interval={0}
                      className="text-xs"
                    />
                    <YAxis />
                    <Tooltip labelFormatter={formatDate} />
                    <Legend />
                    <Line type="monotone" dataKey="publicDataSearches" stroke={COLORS.publicDataSearches} strokeWidth={3} name="Public Data" />
                    <Line type="monotone" dataKey="taxSearches" stroke={COLORS.taxSearches} strokeWidth={2} name="Tax" />
                    <Line type="monotone" dataKey="titleSearches" stroke={COLORS.titleSearches} strokeWidth={2} name="Title (TIQSA)" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Status Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Order Status Distribution</CardTitle>
                <CardDescription>Success vs. failure rate</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Status Trends */}
            <Card>
              <CardHeader>
                <CardTitle>Order Status Trends</CardTitle>
                <CardDescription>Status breakdown over time</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={dataTraceSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDate}
                      angle={-90}
                      textAnchor="end"
                      height={100}
                      interval={0}
                      className="text-xs"
                    />
                    <YAxis />
                    <Tooltip labelFormatter={formatDate} />
                    <Legend />
                    <Bar dataKey="fulfilled" stackId="a" fill={COLORS.fulfilled} name="Fulfilled" />
                    <Bar dataKey="pending" stackId="a" fill={COLORS.pending} name="Pending" />
                    <Bar dataKey="failed" stackId="a" fill={COLORS.failed} name="Failed" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="ai-extraction" className="space-y-4">
          {loadingAiStats ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="text-muted-foreground">Loading AI extraction statistics...</p>
              </div>
            </div>
          ) : aiStatsError ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-destructive">Error Loading AI Extraction Statistics</CardTitle>
                <CardDescription>{aiStatsError}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={fetchAiExtractionStats} variant="outline">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Retry
                </Button>
              </CardContent>
            </Card>
          ) : aiExtractionStats && aiExtractionStats.summary ? (
            <>
          {/* AI Extraction Quality Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Fields</CardTitle>
                <Brain className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{aiExtractionStats.summary.totalFields}</div>
                <p className="text-xs text-muted-foreground">Tracked across {aiExtractionStats.summary.totalOrders} orders</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">AI Extracted</CardTitle>
                <Sparkles className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-success">{aiExtractionStats.summary.aiExtracted}</div>
                <p className="text-xs text-muted-foreground">{aiExtractionStats.summary.overallExtractionRate}% extraction rate</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">High Confidence</CardTitle>
                <CheckCircle className="h-4 w-4 text-success" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-success">{aiExtractionStats.summary.highConfidence}</div>
                <p className="text-xs text-muted-foreground">{Math.round((aiExtractionStats.summary.highConfidence / aiExtractionStats.summary.totalFields) * 100)}% of total fields</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Fields/Order</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{aiExtractionStats.summary.avgFieldsPerOrder}</div>
                <p className="text-xs text-muted-foreground">Across all orders</p>
              </CardContent>
            </Card>
          </div>

          {/* Confidence Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Field Extraction by Confidence Level</CardTitle>
                <CardDescription>Color-coded extraction quality breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Green (High)', value: 14, color: '#10b981' },
                        { name: 'Blue (Verified)', value: 4, color: '#3b82f6' },
                        { name: 'Yellow (Conflict)', value: 3, color: '#f59e0b' },
                        { name: 'Blank (No Data)', value: 2, color: 'hsl(var(--muted-foreground))' }
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {[
                        { name: 'Green (High)', value: 14, color: '#10b981' },
                        { name: 'Blue (Verified)', value: 4, color: '#3b82f6' },
                        { name: 'Yellow (Conflict)', value: 3, color: '#f59e0b' },
                        { name: 'Blank (No Data)', value: 2, color: 'hsl(var(--muted-foreground))' }
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Extraction Rate Over Time</CardTitle>
                <CardDescription>% of fields extracted by AI per order</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={[
                      { date: '2026-02-15', rate: 72 },
                      { date: '2026-02-16', rate: 75 },
                      { date: '2026-02-17', rate: 78 },
                      { date: '2026-02-18', rate: 76 },
                      { date: '2026-02-19', rate: 80 },
                      { date: '2026-02-20', rate: 82 },
                      { date: '2026-02-21', rate: 79 },
                      { date: '2026-02-22', rate: 81 },
                      { date: '2026-02-23', rate: 78 }
                    ]}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(dateStr) => new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis domain={[0, 100]} label={{ value: '% Extracted', angle: -90, position: 'insideLeft' }} />
                    <Tooltip
                      labelFormatter={(dateStr) => new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      formatter={(value) => [`${value}%`, 'Extraction Rate']}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="rate"
                      stroke="hsl(var(--success))"
                      strokeWidth={3}
                      name="Extraction %"
                      dot={{ fill: 'hsl(var(--success))', r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Model Performance Comparison */}
          <Card>
            <CardHeader>
              <CardTitle>AI Model Performance Comparison</CardTitle>
              <CardDescription>Extraction rates by AI model (GPT-4o, Claude 3.5 Sonnet, Gemini 2.5 Flash)</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={[
                  { model: 'GPT-4o', rate: aiExtractionStats.summary.gptOverallRate, color: 'hsl(262, 83%, 58%)' },
                  { model: 'Claude 3.5', rate: aiExtractionStats.summary.claudeOverallRate, color: 'hsl(var(--info))' },
                  { model: 'Gemini 2.5', rate: aiExtractionStats.summary.geminiOverallRate, color: 'hsl(var(--success))' }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="model" />
                  <YAxis domain={[0, 100]} label={{ value: '% Extracted', angle: -90, position: 'insideLeft' }} />
                  <Tooltip formatter={(value) => [`${value}%`, 'Extraction Rate']} />
                  <Legend />
                  <Bar dataKey="rate" name="Extraction Rate" fill="hsl(262, 83%, 58%)" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Field-by-Field Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Field Extraction Details</CardTitle>
              <CardDescription>Breakdown of all {aiExtractionStats.summary.totalFields} fields with extraction statistics and per-model rates</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Field Name</th>
                      <th className="text-left p-2">Category</th>
                      <th className="text-center p-2">Extracted</th>
                      <th className="text-center p-2">Green</th>
                      <th className="text-center p-2">Blue</th>
                      <th className="text-center p-2">Yellow</th>
                      <th className="text-center p-2">Blank</th>
                      <th className="text-center p-2">GPT %</th>
                      <th className="text-center p-2">Claude %</th>
                      <th className="text-center p-2">Gemini %</th>
                      {/* <th className="text-right p-2">Confidence</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {(aiExtractionStats.fieldBreakdown || []).map((row, idx) => (
                      <tr key={idx} className="border-b hover:bg-muted/50">
                        <td className="p-2 font-medium">{row.field}</td>
                        <td className="p-2">
                          <span className="inline-flex items-center rounded-md bg-secondary px-2 py-1 text-xs font-medium text-secondary-foreground">
                            {row.category}
                          </span>
                        </td>
                        <td className="text-center p-2 font-semibold">{row.extracted}</td>
                        <td className="text-center p-2">
                          <span className="font-medium" style={{ color: "#10b981" }}>{row.green}%</span>
                        </td>
                        <td className="text-center p-2">
                          <span className="font-medium" style={{ color: "#3b82f6" }}>{row.blue}%</span>
                        </td>
                        <td className="text-center p-2">
                          <span className="font-medium" style={{ color: "#f59e0b" }}>{row.yellow}%</span>
                        </td>
                        <td className="text-center p-2">
                          <span className="text-muted-foreground font-medium">{row.blank}%</span>
                        </td>
                        <td className="text-center p-2">
                          <span className="font-medium" >{row.gptExtracted}%</span>
                        </td>
                        <td className="text-center p-2">
                          <span className=" font-medium">{row.claudeExtracted}%</span>
                        </td>
                        <td className="text-center p-2">
                          <span className=" font-medium">{row.geminiExtracted}%</span>
                        </td>
                        {/* <td className="text-right p-2">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 h-2 rounded-full overflow-hidden" style={{ backgroundColor: "#e5e7eb" }}>
                              <div
                                className="h-full "
                                style={{ width: `${row.confidence}%`, backgroundColor: "#10b981" }}
                              ></div>
                            </div>
                            <span className="text-xs font-medium w-8">{row.confidence}%</span>
                          </div>
                        </td> */}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 p-4 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>Legend:</strong> Green = Exact agreement between models, Blue = Similar agreement (normalized), Yellow = Conflicts between models, Blank = No data extracted.
                  Model-specific percentages show how often each AI model successfully extracted that field.
                </p>
              </div>
            </CardContent>
          </Card>
            </>
          ) : null}
        </TabsContent>

        <TabsContent value="admin" className="space-y-4">
          {!summary || error ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-destructive">Admin View Data Unavailable</CardTitle>
                <CardDescription>
                  {error || 'The /kpis/usage endpoint is not yet implemented. Only Order Statistics are available.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={fetchKPIData} variant="outline">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Try Loading
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
          {/* Recent Transactions */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Transactions</CardTitle>
              <CardDescription>Last 10 transactions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Timestamp</th>
                      <th className="text-left p-2">Transaction ID</th>
                      <th className="text-left p-2">Address</th>
                      <th className="text-left p-2">Product</th>
                      <th className="text-left p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentTransactions.map((tx) => (
                      <tr key={tx.transactionId} className="border-b">
                        <td className="p-2">{new Date(tx.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                        <td className="p-2 font-mono text-xs">{tx.transactionId.slice(0, 8)}...</td>
                        <td className="p-2">{tx.address}</td>
                        <td className="p-2">
                          <span className="inline-flex items-center rounded-md bg-info/10 px-2 py-1 text-xs font-medium text-info ring-1 ring-inset ring-info/20">
                            {tx.productCode}
                          </span>
                        </td>
                        <td className="p-2">
                          <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                            tx.status === 'FULFILLED' ? 'bg-success/10 text-success ring-1 ring-inset ring-success/20' :
                            tx.status === 'FAILED' ? 'bg-destructive/10 text-destructive ring-1 ring-inset ring-destructive/20' :
                            'bg-warning/10 text-warning ring-1 ring-inset ring-warning/20'
                          }`}>
                            {tx.status || 'UNKNOWN'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Detailed Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Public Data Searches</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{summary.totalPublicDataSearches}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Tax Searches</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{summary.totalTaxSearches}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Title Searches</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{summary.totalTitleSearches}</div>
              </CardContent>
            </Card>
          </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default UsageKPIs;
